import { alpha } from '@mui/material/styles';
import type { ResponsiveStyleValue } from '@mui/system';
import { makeStyles } from 'tss-react/mui';
import { createResponsiveStyles, darkOrLightStyle } from '@/utils/styles';
import { InputSize } from './FormInput';

interface Props {
  isOnDark: boolean | undefined;
  inputSize: ResponsiveStyleValue<InputSize>;
}

const useStyles = makeStyles<Props>({ name: 'FormInput' })(
  ({ brandColors, type, typography: { pxToRem }, breakpoints }, props) => {
    const getInputSizeStyles = (size: InputSize) => {
      switch (size) {
        case InputSize.SM:
          return {
            padding: `${pxToRem(10)} ${pxToRem(16)}`,
            height: pxToRem(44), // fixed height since we don't always know line-height of font
          };
        case InputSize.LG:
          return {
            padding: pxToRem(16),
            height: pxToRem(56), // fixed height since we don't always know line-height of font
          };
        default:
          return {};
      }
    };

    return {
      wrapper: {
        flexBasis: '100%',
      },
      inputContainer: {
        width: '100%',
        marginRight: pxToRem(20),
        '&:last-child': {
          marginRight: 0,
        },
        position: 'relative',
      },
      responsiveSizes: createResponsiveStyles(
        props.inputSize,
        breakpoints,
        getInputSizeStyles
      ),
      input: {
        width: '100%',
        borderRadius: pxToRem(8),
        fontSize: pxToRem(16),
        borderWidth: '1px',
        borderStyle: 'solid',
        outlineStyle: 'none',
        borderColor: darkOrLightStyle(props)(
          alpha(brandColors.white, 0.3),
          alpha(brandColors.black, 0.3)
        ),
        colorScheme: darkOrLightStyle(props)('dark', 'light'),
        color: darkOrLightStyle(props)(
          brandColors.tintsOffWhite40,
          brandColors.tintsNeutral20
        ),
        backgroundColor: darkOrLightStyle(props)(
          brandColors.tintsHeroBlue10,
          brandColors.white
        ),
        marginTop: pxToRem(6),
        '&:read-only': {
          color: brandColors.black,
          backgroundColor: brandColors.tintsHeroBlue90,
          borderColor: brandColors.tintsNeutral30,
        },
      },
      inputLabel: {
        display: 'flex',
        justifyItems: 'center',
        alighItems: 'center',
        ...type['14_medium'],
        letterSpacing: '-0.02rem',
        color: darkOrLightStyle(props)(
          brandColors.tintsOffWhite40,
          brandColors.tintsNeutral20
        ),
      },
      inputHoverActivity: {
        '&:hover:not(:focus)': {
          borderColor: darkOrLightStyle(props)(
            brandColors.white,
            brandColors.black
          ),
        },
      },
      inputFocusActivity: {
        '&:focus': {
          borderWidth: pxToRem(1.5),
          borderColor: brandColors.tintsChargeBlue50,
        },
      },
      inputError: {
        paddingRight: pxToRem(52),
        transition: 'border-color .5s ease',
        borderWidth: pxToRem(1.5),
        borderColor: darkOrLightStyle(props)(
          brandColors.semanticColorsError60,
          brandColors.semanticColorsError50
        ),
      },
      errorColor: {
        color: darkOrLightStyle(props)(
          brandColors.semanticColorsError60,
          brandColors.semanticColorsError50
        ),
      },
      helperText: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        ...type['14_medium'],
        lineHeight: pxToRem(20),
      },
      subtext: {
        color: brandColors.tintsNeutral30,
      },
      bottomTextContainer: {
        minHeight: pxToRem(20),
        marginTop: pxToRem(6),
      },
    };
  }
);

export default useStyles;
